import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ShoppingCart,
  User,
  LogOut,
  LayoutDashboard,
  Store,
  Package,
  Menu,
  X,
  Search,
  MapPin,
  ChevronDown,
  Truck,
  Eye,
  EyeOff,
  MessageCircle,
  ShieldCheck,
  CheckCircle,
  ArrowLeft,
  KeyRound,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { haitiCities } from "../data/haiti_cities";
import CodeInput from "./CodeInput";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8002") + "/api";

export default function Navbar() {
  const { user, login, register, logout, isAuthenticated, refreshUser, forgotPassword, resetPassword, sendVerificationEmail, verifyEmail } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // "tabs" | "verify-email" | "forgot-email" | "forgot-code"
  const [modalView, setModalView] = useState("tabs");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verifyEmailAddress, setVerifyEmailAddress] = useState("");
  const [verifyCode, setVerifyCode] = useState("");

  const [showLocationModal, setShowLocationModal] = useState(false);
  const { shippingAddress, setShippingAddress } = useCart();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    referral_code: "",
  });

  // Polling badge messages non-lus (toutes les 30s)
  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated) { setUnreadMessages(0); return; }
    try {
      const { data } = await axios.get(`${API_URL}/messages/conversations`);
      const total = data.reduce((s, c) => s + (c.unread_count || 0), 0);
      setUnreadMessages(total);
    } catch { /* non-bloquant */ }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogue?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Connexion réussie !");
      setShowAuthModal(false);
      setLoginData({ email: "", password: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur de connexion";
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const openAuthModal = () => {
    setModalView("tabs");
    setForgotEmail("");
    setResetCode("");
    setNewPassword("");
    setShowAuthModal(true);
  };

  const handleForgotSendCode = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success("Si cet email existe, un code a été envoyé.");
      setResetCode("");
      setModalView("forgot-code");
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetCode.length < 6) return toast.error("Entrez les 6 chiffres du code");
    if (newPassword.length < 6) return toast.error("Le mot de passe doit faire au moins 6 caractères");
    setAuthLoading(true);
    try {
      await resetPassword(forgotEmail, resetCode, newPassword);
      toast.success("Mot de passe réinitialisé ! Connectez-vous.");
      setLoginData({ email: forgotEmail, password: "" });
      setModalView("tabs");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== confirmPassword) {
      return toast.error("Les mots de passe ne correspondent pas");
    }
    setAuthLoading(true);
    try {
      await register(registerData);
      toast.success("Compte créé ! Vérifiez votre email.");
      setVerifyEmailAddress(registerData.email.toLowerCase());
      setVerifyCode("");
      setConfirmPassword("");
      setRegisterData({ email: "", password: "", first_name: "", last_name: "", phone: "", referral_code: "" });
      setModalView("verify-email");
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (verifyCode.length < 6) return toast.error("Entrez les 6 chiffres du code");
    setAuthLoading(true);
    try {
      const result = await verifyEmail(verifyEmailAddress, verifyCode);
      toast.success("Email vérifié ! Bienvenue !");
      setShowAuthModal(false);
      setModalView("tabs");
      const isAdmin = result?.user?.roles?.some((r) => r.role_type === "ADMIN");
      navigate(isAdmin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendVerificationEmail(verifyEmailAddress);
      toast.success("Nouveau code envoyé !");
    } catch {
      toast.error("Impossible de renvoyer le code");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Déconnexion réussie");
  };

  const categories = [
    { name: "Électronique", slug: "electronique" },
    { name: "Mode", slug: "mode" },
    { name: "Maison", slug: "maison" },
    { name: "Sport", slug: "sport" },
    { name: "Beauté", slug: "beaute" },
    { name: "Alimentation", slug: "alimentation" },
    { name: "Jouets", slug: "jouets" },
    { name: "Auto", slug: "auto" },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 bg-slate-900 text-white shadow-lg">
        {/* Top bar */}
        <div className="bg-slate-800 py-1 text-xs hidden md:block">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Truck className="h-3 w-3" />
                Livraison partout en Haïti
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth" className="hover:text-orange-400">Devenir vendeur</Link>
              <Link to="/aide" className="hover:text-orange-400">Aide</Link>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo">
              <img src="/logo.png" alt="BuyMoreEasy" className="h-12 w-auto" />
            </Link>

            {/* Delivery Location Selector */}
            <button
              className="hidden lg:flex items-center gap-1 text-sm hover:text-orange-400 transition-colors"
              onClick={() => setShowLocationModal(true)}
            >
              <MapPin className="h-4 w-4" />
              <div className="text-left">
                <div className="text-xs text-gray-400">Livrer à</div>
                <div className="font-medium truncate max-w-[150px]">
                  {shippingAddress?.city || shippingAddress?.address || "Sélectionner adresse"}
                </div>
              </div>
            </button>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="w-full order-last md:flex-1 md:w-auto md:max-w-3xl md:order-none">
              <div className="relative flex">
                <Input
                  type="text"
                  placeholder="Rechercher des produits, boutiques..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-r-none border-0 bg-white text-black pl-4 pr-10 h-11 focus-visible:ring-orange-500"
                />
                <Button
                  type="submit"
                  className="rounded-l-none bg-orange-500 hover:bg-orange-600 h-11 px-6"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </form>

            {/* Action Buttons */}
            <div className="flex items-center gap-1 sm:gap-2 ml-auto md:ml-0">
              {/* Account */}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-white hover:text-orange-400 hover:bg-slate-800 px-2 sm:px-3"
                    >
                      <div className="text-left hidden sm:block">
                        <div className="text-xs text-gray-400">Bonjour, {user?.first_name}</div>
                        <div className="font-medium text-sm flex items-center gap-1">
                          Compte <ChevronDown className="h-3 w-3" />
                        </div>
                      </div>
                      <User className="h-6 w-6 sm:hidden" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-sm font-medium">
                      {user?.first_name} {user?.last_name}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Tableau de bord
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/commandes")}>
                      <Package className="mr-2 h-4 w-4" />
                      Mes retours & Commandes
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/mes-boutiques")}>
                      <Store className="mr-2 h-4 w-4" />
                      Mes boutiques
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/messages")}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Messages
                      {unreadMessages > 0 && (
                        <span className="ml-auto bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                          {unreadMessages > 99 ? "99+" : unreadMessages}
                        </span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/profil")}>
                      <User className="mr-2 h-4 w-4" />
                      Mon profil
                    </DropdownMenuItem>
                    {user?.roles?.some(r => r.role_type === 'admin') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/admin")}>
                          <div className="flex items-center text-purple-600 font-medium">
                            <ShieldCheck className="mr-2 h-4 w-4" />
                            Administration
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="ghost"
                  className="text-white hover:text-orange-400 hover:bg-slate-800 px-2 sm:px-3"
                  onClick={openAuthModal}
                >
                  <div className="text-left hidden sm:block">
                    <div className="text-xs text-gray-400">Bonjour</div>
                    <div className="font-medium text-sm">Connexion</div>
                  </div>
                  <User className="h-6 w-6 sm:hidden" />
                </Button>
              )}

              {/* Orders */}
              <Link to={isAuthenticated ? "/commandes" : "#"} onClick={() => !isAuthenticated && openAuthModal()}>
                <Button
                  variant="ghost"
                  className="text-white hover:text-orange-400 hover:bg-slate-800 px-2 sm:px-3"
                >
                  <div className="text-left hidden sm:block">
                    <div className="text-xs text-gray-400">Mes</div>
                    <div className="font-medium text-sm">Retours & Cmds</div>
                  </div>
                  <Package className="h-6 w-6 sm:hidden" />
                </Button>
              </Link>

              {/* Messages (Desktop) */}
              <Link to={isAuthenticated ? "/messages" : "#"} onClick={() => !isAuthenticated && openAuthModal()}>
                <Button
                  variant="ghost"
                  className="text-white hover:text-orange-400 hover:bg-slate-800 px-2 sm:px-3 hidden md:flex relative"
                >
                  <MessageCircle className="h-6 w-6" />
                  {unreadMessages > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs min-w-[18px] h-[18px] flex items-center justify-center p-0 rounded-full">
                      {unreadMessages > 99 ? "99+" : unreadMessages}
                    </Badge>
                  )}
                </Button>
              </Link>

              {/* Cart */}
              <Link to="/panier">
                <Button
                  variant="ghost"
                  className="text-white hover:text-orange-400 hover:bg-slate-800 px-2 sm:px-3 relative"
                >
                  <ShoppingCart className="h-6 w-6" />
                  {itemCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs min-w-[20px] h-5 flex items-center justify-center">
                      {itemCount}
                    </Badge>
                  )}
                  <span className="hidden sm:inline ml-1 font-medium">Panier</span>
                </Button>
              </Link>

              {/* Mobile menu */}
              <Button
                variant="ghost"
                className="text-white hover:bg-slate-800 md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="bg-slate-800 border-t border-slate-700">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2">
              <Link
                to="/catalogue"
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium hover:bg-slate-700 rounded whitespace-nowrap"
              >
                <Menu className="h-4 w-4" />
                Tout
              </Link>
              <Link
                to="/boutiques"
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium hover:bg-slate-700 rounded whitespace-nowrap text-orange-200"
              >
                <Store className="h-4 w-4" />
                Boutiques & Artisans
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/catalogue?category=${cat.slug}`}
                  className="px-3 py-1.5 text-sm hover:bg-slate-700 rounded whitespace-nowrap transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/mes-boutiques"
                className="px-3 py-1.5 text-sm text-orange-400 hover:bg-slate-700 rounded whitespace-nowrap"
              >
                Vendre
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700 py-4 px-4">
            <div className="space-y-2">
              <Link
                to="/catalogue"
                className="block py-2 hover:text-orange-400"
                onClick={() => setMobileMenuOpen(false)}
              >
                Catalogue
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/dashboard"
                    className="block py-2 hover:text-orange-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Tableau de bord
                  </Link>
                  <Link
                    to="/mes-boutiques"
                    className="block py-2 hover:text-orange-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mes boutiques
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={(open) => { setShowAuthModal(open); if (!open) setModalView("tabs"); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center flex justify-center">
              <img src="/logo.png" alt="BuyMoreEasy" className="h-16 w-auto" />
            </DialogTitle>
            <DialogDescription className="sr-only">Formulaire de connexion et d'inscription</DialogDescription>
          </DialogHeader>

          {/* ── Vue : Login / Register ───────────────────────────────── */}
          {modalView === "tabs" && (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Connexion</TabsTrigger>
                <TabsTrigger value="register">Inscription</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="modal-login-email">Email</Label>
                    <Input
                      id="modal-login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-login-password">Mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="modal-login-password"
                        type={showPassword ? "text" : "password"}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {/* Lien mot de passe oublié */}
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm text-orange-500 hover:text-orange-600 hover:underline"
                      onClick={() => { setForgotEmail(loginData.email); setModalView("forgot-email"); }}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={authLoading}>
                    {authLoading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="modal-reg-firstname">Prénom</Label>
                      <Input
                        id="modal-reg-firstname"
                        value={registerData.first_name}
                        onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="modal-reg-lastname">Nom</Label>
                      <Input
                        id="modal-reg-lastname"
                        value={registerData.last_name}
                        onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-reg-email">Email</Label>
                    <Input
                      id="modal-reg-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-reg-password">Mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="modal-reg-password"
                        type={showPassword ? "text" : "password"}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-reg-confirm-password">Confirmer le mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="modal-reg-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className={confirmPassword && confirmPassword !== registerData.password ? "border-red-400 focus-visible:ring-red-400" : ""}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== registerData.password && (
                      <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-reg-referral">Code Parrainage (Optionnel)</Label>
                    <Input
                      id="modal-reg-referral"
                      value={registerData.referral_code}
                      onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value })}
                      placeholder="Entrez le code si vous en avez un"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={authLoading || (confirmPassword.length > 0 && confirmPassword !== registerData.password)}
                  >
                    {authLoading ? "Création..." : "Créer un compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}

          {/* ── Vue : Vérification email après inscription ────────────── */}
          {modalView === "verify-email" && (
            <div className="pt-2">
              <div className="flex flex-col items-center text-center mb-5">
                <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                  <Mail className="h-6 w-6 text-orange-500" />
                </div>
                <p className="text-sm font-semibold text-gray-800">Vérifiez votre adresse email</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Un code à 6 chiffres a été envoyé à<br />
                  <strong className="text-foreground">{verifyEmailAddress}</strong>
                </p>
              </div>
              <form onSubmit={handleVerifyEmail}>
                <CodeInput value={verifyCode} onChange={setVerifyCode} />
                <Button
                  type="submit"
                  className="w-full mt-2 bg-orange-500 hover:bg-orange-600"
                  disabled={authLoading || verifyCode.length < 6}
                >
                  {authLoading ? "Vérification..." : "Confirmer mon email"}
                </Button>
              </form>
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={handleResendVerification}
                >
                  Renvoyer le code
                </button>
              </div>
            </div>
          )}

          {/* ── Vue : Saisie email pour reset ─────────────────────────── */}
          {modalView === "forgot-email" && (
            <div className="pt-2">
              <div className="flex flex-col items-center text-center mb-5">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                  <KeyRound className="h-6 w-6 text-blue-500" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Entrez votre email pour recevoir un code de réinitialisation.
                </p>
              </div>
              <form onSubmit={handleForgotSendCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-forgot-email">Email</Label>
                  <Input
                    id="modal-forgot-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={authLoading}>
                  {authLoading ? "Envoi..." : "Envoyer le code"}
                </Button>
              </form>
              <button
                type="button"
                className="mt-4 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                onClick={() => setModalView("tabs")}
              >
                <ArrowLeft className="h-3 w-3" /> Retour à la connexion
              </button>
            </div>
          )}

          {/* ── Vue : Code + nouveau mot de passe ─────────────────────── */}
          {modalView === "forgot-code" && (
            <div className="pt-2">
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Code envoyé à <strong className="text-foreground">{forgotEmail}</strong>
                </p>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-center block mb-1">Code à 6 chiffres</Label>
                  <CodeInput value={resetCode} onChange={setResetCode} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-new-password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="modal-new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={authLoading || resetCode.length < 6 || newPassword.length < 6}
                >
                  {authLoading ? "Réinitialisation..." : "Changer le mot de passe"}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-3">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={handleForgotSendCode}
                >
                  Renvoyer le code
                </button>
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                  onClick={() => setModalView("tabs")}
                >
                  <ArrowLeft className="h-3 w-3" /> Connexion
                </button>
              </div>
            </div>
          )}

        </DialogContent>
      </Dialog>

      {/* Location Modal */}
      <Dialog open={showLocationModal} onOpenChange={setShowLocationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choisir l'adresse de livraison</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto pt-4">
            {!isAuthenticated ? (
              <div className="text-center p-4">
                <p className="text-gray-500 mb-4">Connectez-vous pour choisir une de vos adresses enregistrées.</p>
                <Button onClick={() => { setShowLocationModal(false); openAuthModal(); }}>Se connecter</Button>
              </div>
            ) : user?.saved_addresses?.length > 0 ? (
              user.saved_addresses.map(addr => (
                <div
                  key={addr.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${shippingAddress?.id === addr.id ? 'border-orange-500 bg-orange-50' : 'hover:bg-gray-50'}`}
                  onClick={async () => {
                    setShippingAddress(addr);
                    try {
                      await axios.put(`${API_URL}/users/profile`, { default_address_id: addr.id });
                      await refreshUser(); // Synchroniser avec LocalStorage
                    } catch (e) {
                      console.error("Impossible de définir comme adresse par défaut:", e);
                    }
                    setShowLocationModal(false);
                    toast.success(`Livraison définie à ${addr.city || 'cette adresse'}`);
                    setTimeout(() => window.location.reload(), 200);
                  }}
                >
                  <MapPin className={`h-5 w-5 ${shippingAddress?.id === addr.id ? 'text-orange-500' : 'text-gray-400'}`} />
                  <div className="flex-1">
                    <div className="font-medium">{addr.name || 'Adresse'}</div>
                    <div className="text-xs text-gray-500">{addr.address}, {addr.city}</div>
                  </div>
                  {shippingAddress?.id === addr.id && <CheckCircle className="h-5 w-5 text-orange-500" />}
                </div>
              ))
            ) : (
              <div className="text-center p-4">
                <p className="text-gray-500 mb-4">Vous n'avez pas encore d'adresse enregistrée.</p>
                <Button onClick={() => { setShowLocationModal(false); navigate("/profil"); }}>Ajouter une adresse</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
